import { action } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { tracked } from "@glimmer/tracking"
import type { Permission } from "@shared/models/permissions"
import type { AiValueEditor } from "cc-frontend/lib/ai/ai-value-editor"
import type LessonEditor from "cc-frontend/services/lesson-editor"
import type { ModelsNeededForFirstEdit } from "cc-frontend/services/lesson-editor"
import styles from "./plaintext-value.module.css"

interface Signature {
  Args: {
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit
    value: string | null
    placeholder: string
    permissions: Array<Permission>
    cardStackId: string
    cardId: string
    registerCardEditor?: (cardId: string, prop: string, editor: AiValueEditor<unknown>) => void
  }
}

export default class PlaintextValue extends Component<Signature> implements AiValueEditor<string> {
  readonly styles = styles

  @service declare lessonEditor: LessonEditor

  @tracked isAiEditing = false
  @tracked aiValue: string = ""

  constructor(owner: unknown, args: Signature["Args"]) {
    super(owner, args)

    this.args.registerCardEditor?.(this.args.cardId, "value", this)
  }

  @action
  updateSection(id, prop, value) {
    if (this.isAiEditing) return

    this.lessonEditor.editCard(
      this.args.modelsNeededForFirstEdit,
      this.args.cardStackId,
      id,
      prop,
      value
    )
  }

  @action
  finishedEditing(id, prop, value, originalValue, wordCountChange) {
    if (this.isAiEditing) return

    this.lessonEditor.finishEditingCard(
      this.args.modelsNeededForFirstEdit,
      this.args.cardStackId,
      id,
      prop,
      value,
      originalValue,
      wordCountChange
    )
  }

  // AiValueEditor methods

  startAiEdits(): void {
    this.isAiEditing = true
    this.aiValue = this.args.value || ""
  }

  setValueFromAi(aiValue: string): void {
    this.aiValue = aiValue
  }

  acceptAiEdits(): string {
    const result = this.aiValue
    this.isAiEditing = false
    this.aiValue = ""

    return result
  }

  rejectAiEdits(): void {
    if (!this.isAiEditing) return

    this.isAiEditing = false
    this.aiValue = ""
  }
}
